import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth } from './AuthContext';

type SubscriptionTier = 'free' | 'pro' | 'teams' | 'enterprise';

interface FeatureFlags {
  // Core Features
  canCreateMultipleCards: boolean;
  canCreateUnlimitedCards: boolean;
  canUsePremiumTemplates: boolean;
  canRemoveBranding: boolean;
  canUseCustomDomain: boolean;
  canUseNFC: boolean;
  canUseWalletPass: boolean;
  canShareQR: boolean;

  // Analytics Features
  canViewAnalyticsDashboard: boolean;
  canViewAdvancedAnalytics: boolean;
  canExportData: boolean;

  // Integration Features
  canUseCRMIntegrations: boolean;
  canUseAPI: boolean;
  canUseCustomIntegrations: boolean;
  canUseSSO: boolean;

  // Team Features
  canManageTeam: boolean;
  canUseBrandConsistency: boolean;
  canUseAdminDashboard: boolean;
  canBulkCreateCards: boolean;

  // Enterprise Features
  canWhiteLabel: boolean;
  hasDedicatedAccountManager: boolean;
  hasSLA: boolean;
  hasPrioritySupport: boolean;

  // Gifts
  canCreateGifts: boolean;

  // Limits
  maxCards: number | null; // null = unlimited
  maxContacts: number | null;
  maxTeamMembers: number | null;
  maxGifts: number | null; // null = unlimited, 1 for free tier
}

const DEFAULT_FEATURES: FeatureFlags = {
  // Free tier defaults
  canCreateMultipleCards: false,
  canCreateUnlimitedCards: false,
  canUsePremiumTemplates: false,
  canRemoveBranding: false,
  canUseCustomDomain: false,
  canUseNFC: false,
  canUseWalletPass: true,     // Free tier includes wallet pass
  canShareQR: true,            // Free tier includes QR sharing
  canViewAnalyticsDashboard: false,
  canViewAdvancedAnalytics: false,
  canExportData: false,
  canUseCRMIntegrations: false,
  canUseAPI: false,
  canUseCustomIntegrations: false,
  canUseSSO: false,
  canManageTeam: false,
  canUseBrandConsistency: false,
  canUseAdminDashboard: false,
  canBulkCreateCards: false,
  canWhiteLabel: false,
  hasDedicatedAccountManager: false,
  hasSLA: false,
  hasPrioritySupport: false,
  canCreateGifts: true,
  maxCards: 1,
  maxContacts: 25,
  maxTeamMembers: null,
  maxGifts: 1,
};

const getFeaturesForTier = (tier: SubscriptionTier): FeatureFlags => {
  const baseFeatures = { ...DEFAULT_FEATURES };

  switch (tier) {
    case 'enterprise':
      return {
        ...baseFeatures,
        canCreateMultipleCards: true,
        canCreateUnlimitedCards: true,
        canUsePremiumTemplates: true,
        canRemoveBranding: true,
        canUseCustomDomain: true,
        canUseNFC: true,
        canViewAnalyticsDashboard: true,
        canViewAdvancedAnalytics: true,
        canExportData: true,
        canUseCRMIntegrations: true,
        canUseAPI: true,
        canUseCustomIntegrations: true,
        canUseSSO: true,
        canManageTeam: true,
        canUseBrandConsistency: true,
        canUseAdminDashboard: true,
        canBulkCreateCards: true,
        canWhiteLabel: true,
        hasDedicatedAccountManager: true,
        hasSLA: true,
        hasPrioritySupport: true,
        maxCards: null,
        maxContacts: null,
        maxTeamMembers: null,
        maxGifts: null,
      };

    case 'teams':
      return {
        ...baseFeatures,
        canCreateMultipleCards: true,
        canCreateUnlimitedCards: true,
        canUsePremiumTemplates: true,
        canRemoveBranding: true,
        canUseCustomDomain: true,
        canUseNFC: true,
        canViewAnalyticsDashboard: true,
        canViewAdvancedAnalytics: false,
        canExportData: true,
        canUseCRMIntegrations: true,
        canUseSSO: true,
        canManageTeam: true,
        canUseBrandConsistency: true,
        canUseAdminDashboard: true,
        canBulkCreateCards: true,
        hasPrioritySupport: true,
        maxCards: null,
        maxContacts: 1000,
        maxTeamMembers: null,
        maxGifts: null,
      };

    case 'pro':
      return {
        ...baseFeatures,
        canCreateMultipleCards: true,
        canCreateUnlimitedCards: true,
        canUsePremiumTemplates: true,
        canRemoveBranding: true,
        canUseCustomDomain: true,
        canUseNFC: true,
        canViewAnalyticsDashboard: true,
        canExportData: true,
        canUseCRMIntegrations: true,
        maxCards: null,
        maxContacts: 500,
        maxGifts: null,
      };

    case 'free':
    default:
      return baseFeatures;
  }
};

const FeatureFlagsContext = createContext<FeatureFlags>(DEFAULT_FEATURES);

export const FeatureFlagsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();

  const userTier: SubscriptionTier = user?.subscription?.plan?.toLowerCase() as SubscriptionTier || 'free';

  const features = getFeaturesForTier(userTier);

  return (
    <FeatureFlagsContext.Provider value={features}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlags => {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};

// Helper hooks for specific features
export const useCanCreateMultipleCards = () => useFeatureFlags().canCreateMultipleCards;
export const useCanUsePremiumTemplates = () => useFeatureFlags().canUsePremiumTemplates;
export const useCanRemoveBranding = () => useFeatureFlags().canRemoveBranding;
export const useCanViewAdvancedAnalytics = () => useFeatureFlags().canViewAdvancedAnalytics;
export const useCanExportData = () => useFeatureFlags().canExportData;
export const useCanManageTeam = () => useFeatureFlags().canManageTeam;
export const useCanUseAPI = () => useFeatureFlags().canUseAPI;
export const useCanUseSSO = () => useFeatureFlags().canUseSSO;
export const useMaxContacts = () => useFeatureFlags().maxContacts;
export const useMaxTeamMembers = () => useFeatureFlags().maxTeamMembers;
export const useCanCreateGifts = () => useFeatureFlags().canCreateGifts;
export const useMaxGifts = () => useFeatureFlags().maxGifts;
